import React from 'react';
import { useApiClient } from '@m43/core';
import { HospitalsResource, NudgeKeyValuesEnum } from '@mark43/cad-api';
import {
    CadNextSocketListener,
    createStreamListener,
    useSocketStatus,
    useStream,
} from '../socket/use-stream';
import { WorkspacePanel } from '../panels/workspace-panel';
import { workspacePanelIsQueue } from '../panels/workspace-queue-exists';
import { useQuery } from '@tanstack/react-query';
import { handleStoreUpdates } from '../queues/utils/handle-store-updates';
import { PromiseBuffer } from '../common/promises/promise-buffer';
import { HospitalQueueStore } from './store/hospital-queue-store';

interface IntegratedHospitalQueueStoreProps {
    workspacePanel: WorkspacePanel;
    dispatchAreaIds: number[];
    store: HospitalQueueStore;
    filteredHospitalCapabilityAttributeIds: number[];
    filteredHospitalStatusAttributeIds: number[];
}

/**
 * This should never be imported directly outside hospital-queue-state-provider
 */
export function useIntegratedHospitalQueueStore({
    workspacePanel,
    dispatchAreaIds,
    store,
    filteredHospitalCapabilityAttributeIds,
    filteredHospitalStatusAttributeIds,
}: IntegratedHospitalQueueStoreProps): HospitalQueueStore {
    workspacePanelIsQueue(workspacePanel);

    const [promiseBuffer] = React.useState(
        () =>
            new PromiseBuffer<number>({
                delay: 100,
                callback: (inputs) => {
                    return fetchHospitalUpdatesByIds(inputs);
                },
            })
    );

    const apiClient = useApiClient();

    const getInitialQueueStateQuery = useQuery({
        queryKey: [
            'get-initial-hospital-queue-views',
            store.workspacePanel.queue.id,
            dispatchAreaIds,
        ],
        queryFn() {
            promiseBuffer.stop();
            promiseBuffer.reset();
            return apiClient.request<HospitalsResource.GetDepartmentHospitalQueueViews>({
                method: 'POST',
                url: 'hospitals/queue_views',
                data: {
                    hospitalCapabilityAttributeIds: filteredHospitalCapabilityAttributeIds,
                    hospitalStatusAttributeIds: filteredHospitalStatusAttributeIds,
                    includeAll: false,
                    hospitalIds: [],
                },
            });
        },
        onSuccess(response) {
            if (response.data.data?.hospitalQueueViews) {
                store.setRows(response.data.data.hospitalQueueViews);
            }
            promiseBuffer.start();
        },
        onError() {
            store.setIsError(true);
        },
        notifyOnChangeProps: [],
    });

    const refetch = getInitialQueueStateQuery.refetch;

    const socketStatusListener: CadNextSocketListener = React.useMemo(
        () => ({
            invokeOnInitialize: true,
            onSocketStatusChange: (socketStatusChangeEvent) => {
                if (socketStatusChangeEvent.status === 'CONNECTED') {
                    refetch();
                }
            },
        }),
        [refetch]
    );

    const { getSocketStatus } = useSocketStatus(socketStatusListener);

    React.useEffect(() => {
        if (getSocketStatus() === 'CONNECTED') {
            refetch();
        }
    }, [dispatchAreaIds, refetch, getSocketStatus]);

    const fetchHospitalUpdatesByIds = React.useMemo(() => {
        return (hospitalIds: number[]) => {
            return apiClient
                .request<HospitalsResource.GetHospitalQueueViewsByIds>({
                    method: 'POST',
                    url: 'hospitals/queue_views/ids/',
                    data: {
                        hospitalIds,
                        hospitalCapabilityAttributeIds: filteredHospitalCapabilityAttributeIds,
                        hospitalStatusAttributeIds: filteredHospitalStatusAttributeIds,
                        includeAll: false,
                    },
                })
                .then((response) => {
                    handleStoreUpdates(store, hospitalIds, response.data.data?.hospitalQueueViews);
                });
        };
    }, [
        apiClient,
        filteredHospitalCapabilityAttributeIds,
        filteredHospitalStatusAttributeIds,
        store,
    ]);

    const listener = React.useMemo(
        () =>
            createStreamListener({
                streamNames: ['nudge'],
                onMessage: (message) => {
                    const payload = message.payload;
                    const hospitalIdsToUpdate: number[] = [
                        ...(payload.map['H'] // HOSPITAL, any hospital info change
                            ?.map((n) => n.id) ?? []),
                        ...(payload.map['USL'] // UNIT_STATUS_LINK_KEY, if unit's status link invloves HOSPITAL related change, the corresponding hospital (identified by uslEId) in hospital queue need to be updated for unit column in queue.
                            ?.filter(
                                (n) => n.uslEType === NudgeKeyValuesEnum.HOSPITAL.name && !!n.uslEId
                            )
                            .map((n) => n.uslEId!) ?? []),
                    ];

                    if (!hospitalIdsToUpdate.length) {
                        return;
                    }

                    for (const hospitalId of hospitalIdsToUpdate) {
                        promiseBuffer.enqueue(hospitalId);
                    }
                },
            }),
        [promiseBuffer]
    );

    useStream(listener);

    return store;
}
